import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ExportType } from '@/types/export';
import { safeExportFileBaseName } from '@/utils/file-name';
import { clipExportMessages } from './useEventsExport';
import { useExportJob } from './useExportJob';

const CLIP_EXPORT_TOAST_ID = 'report-event-clip-export';

interface ReportEventClipTarget {
    id: string;
    promptText: string | null;
}

interface UseReportEventClipExportResult {
    busy: boolean;
    startExport: (event: ReportEventClipTarget) => Promise<boolean>;
}

export function useReportEventClipExport(
    reportId: string,
): UseReportEventClipExportResult {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const messages = useMemo(() => clipExportMessages(t), [t]);
    const { busy, start } = useExportJob({
        toastId: CLIP_EXPORT_TOAST_ID,
        messages,
    });

    const startExport = useCallback(
        (event: ReportEventClipTarget) =>
            start(
                ExportType.REPORT,
                { reportId, eventIds: [event.id], clip: true },
                `${safeExportFileBaseName(event.promptText ?? '', 'event')}.mp4`,
            ),
        [reportId, start],
    );

    return { busy, startExport };
}
